import {
  SPARTACUS_DEFAULT_RANGE_DAYS,
  SPARTACUS_TRACKING_START_DATE,
} from './spartacus'
import { formatSpartacusUkDate } from 'src/utils/spartacusDateRange'

const trackingStartLabel = formatSpartacusUkDate(SPARTACUS_TRACKING_START_DATE)

/** Shared copy — append concrete dates via getSpartacusPriorPeriodTooltip() where the range is known. */
export const SPARTACUS_PRIOR_PERIOD_DEFINITION =
  'The prior equivalent period is the same number of days as your selected range, ending the day before your range starts (UK time). It is not calendar month vs month.'

export const SPARTACUS_COLUMN_TOOLTIPS = {
  venueName: 'Venue name as shown in OMS admin.',
  phone:
    'Venue phone number from OMS settings. Click to call on a phone or softphone.',
  plan: 'Current OMS plan (Free, Starter, Growth, or Pro) from usage limits.',
  channel:
    'Sales channel: TFR, GREEN, JUST, or Direct when the venue is not on a brand programme.',
  createdAt: 'When the venue was first created in OMS.',
  lastOrderedAt:
    'Date of the venue’s most recent order (any time, not limited to the selected range).',
  ordersInPeriod: `Distinct purchase orders in the selected UK date range (default last ${SPARTACUS_DEFAULT_RANGE_DAYS} days).`,
  orderTrend: `Change in order count vs the prior equivalent period. ${SPARTACUS_PRIOR_PERIOD_DEFINITION} New = venue created in the current range. No prior activity = zero orders in the prior window.`,
  upgradeScore:
    'Upgrade potential from 0–100 for the selected UK date range. Higher scores mean stronger upgrade conversations. Based on plan headroom, order momentum, feature depth, commercial readiness, and team breadth. Prioritise outreach when the score is high and drivers show commercial readiness.',
  riskScore:
    'Retention risk from 0–100 for the selected UK date range. Higher scores need proactive outreach. Based on engagement decline, period dormancy, login inactivity (including no login recorded after last-seen tracking begins, once the rollout grace period has passed), feature dormancy, commercial fragility, and lifecycle signals. Prioritise check-ins when the score is high and orders are declining.',
  activeSuppliers:
    'Count of active, non-dismissed suppliers configured on the venue. Red = 0, amber = 1–4 (below goal), green = 5+ (at goal).',
  parLevelsUsed:
    'Products with a par level above zero on the venue shopping list.',
  restrictedSuppliers:
    'Suppliers with order approval turned on (restricted with approval emails configured).',
  specialPurchasesInPeriod:
    'One-off special purchase requests created in the selected date range.',
  recipesCount: 'Total recipes on the venue (all time).',
  stockTakesCount: 'Total stock takes recorded on the venue (all time).',
  xeroConnected:
    'Whether Xero accounting is currently connected for this venue.',
  gpUsing:
    'Whether the venue recorded any sales/GP data in the selected date range.',
  gpPercent:
    'Gross profit percentage for the selected period: (sales − OMS purchases) ÷ sales.',
}

export const SPARTACUS_STATS_TOOLTIPS = {
  totalVenues:
    'Venues matching the current filters in the selected date range.',
  totalOrders:
    'Total distinct purchase orders across filtered venues in the selected period.',
  freeVenuePct: 'Share of filtered venues on the Free plan.',
  paidVenuePct:
    'Share of filtered venues on a paid plan (Starter, Growth, or Pro).',
  activationPct:
    'Venues with active suppliers, orders in period, and at least one advanced feature in use.',
  activeVenueCount: 'Venues with at least one order in the selected period.',
  dormantVenueCount: 'Filtered venues with zero orders in the selected period.',
  highIntentFreeCount:
    'Free venues with 3+ orders in period and advanced feature usage.',
  retentionPulse: `Paid venues with declining orders plus free venues with declining orders vs the prior equivalent period. ${SPARTACUS_PRIOR_PERIOD_DEFINITION}`,
  delta: `Change vs the prior equivalent period for the same filter set. ${SPARTACUS_PRIOR_PERIOD_DEFINITION}`,
}

export const SPARTACUS_SCORE_DRIVER_TOOLTIPS = {
  upgrade:
    'Top reasons contributing to this venue’s upgrade score. Click the score to review drivers before outreach.',
  risk: 'Top reasons contributing to this venue’s risk score. Click the score to review drivers before retention outreach.',
}

export const SPARTACUS_LAST_SEEN_COLUMN_TOOLTIP = `Last user login tracked from ${trackingStartLabel} (UK). Accounts that have not logged in since then show Not tracked yet.`

export const SPARTACUS_LAST_VENUE_ACTIVITY_TOOLTIP =
  'Latest order, recipe, or stock take on the venue. Useful when login tracking is not available yet.'

export const SPARTACUS_FILTER_TOOLTIPS = {
  preset:
    'Ready-made views for common sales tasks. Pick one to apply its filters — you can still tweak individual fields afterwards.',
  search:
    'Look up venues by name or by a user’s email. Partial venue names work. For email, include @ — we match the account’s primary email, username, and secondary email.',
  plan: 'Show venues on a specific OMS plan: Free, Starter, Growth, or Pro.',
  channel:
    'Which brand or programme the venue sits under: TFR, GREEN, JUST, or Direct (no brand programme).',
  paidPlan: 'Yes = Starter, Growth, or Pro only. No = Free plan only.',
  orderTrend: `How ordering compares to the prior equivalent period: rising, falling, steady, brand-new venue, or nothing to compare. ${SPARTACUS_PRIOR_PERIOD_DEFINITION}`,
  ordersInPeriodMin:
    'Only include venues with at least this many orders in your selected date range.',
  activeSupplierBuckets:
    'Filter venues by how many active suppliers they use. Pick one or more counts, or use the quick chips for below goal (0–4) or at goal (5+).',
  xeroConnected: 'Whether the venue has linked Xero for accounting.',
  gpUsing:
    'Whether the venue recorded any sales or gross-profit data in the selected date range.',
  hasLastSeen:
    'Whether we have a recent user login on record. Older venues may show as not tracked yet.',
  hasParLevels:
    'Venues that set par (minimum stock) levels on products in their shopping list.',
  hasRecipes: 'Venues that have created at least one recipe in OMS.',
  hasStockTakes: 'Venues that have recorded at least one stock take.',
  hasSpecialPurchases:
    'Venues with one-off special purchase requests in the selected date range.',
  hasRestrictedSuppliers:
    'Venues with suppliers that require order approval before purchasing.',
  hasFeatureUsage:
    'Venues using any advanced feature: par levels, recipes, stock takes, special purchases, restricted suppliers, or GP/sales data in the period.',
  upgradeScoreMin:
    'Minimum upgrade potential (0–100). Higher scores mean stronger upgrade conversations — see the Upgrade score column in the table.',
  upgradeScoreMax:
    'Maximum upgrade potential (0–100). Leave blank for no upper limit.',
  riskScoreMin:
    'Minimum retention risk (0–100). Higher scores mean venues that need a proactive check-in — see the Risk score column.',
  riskScoreMax:
    'Maximum retention risk (0–100). Leave blank for no upper limit.',
}

export const SPARTACUS_TOOLTIP_KEYS = Object.keys(SPARTACUS_COLUMN_TOOLTIPS)

export const SPARTACUS_STATS_TOOLTIP_KEYS = Object.keys(
  SPARTACUS_STATS_TOOLTIPS,
)

export const SPARTACUS_SEARCH_FILTER_LABEL = 'Search venue or email'
export const SPARTACUS_SEARCH_FILTER_PLACEHOLDER = 'Venue name or email'
